/**
 * CRM-A24: Pazarlama bölümünün rıza ekranının metinleri.
 *
 * `metin/ortak.ts`in kuralı burada da geçerli: metin **veri**, koda serpiştirilmiş dize değil.
 * Kod değerleri (`GRANTED`, `PENDING`, `NO_RECORD`) çevrilmiyor, yalnız ekrana yazılırken
 * karşılığı gösteriliyor; karşılığı olmayan bir kod geldiğinde kodun kendisi yazılıyor, çünkü
 * eksik olan görünür, uydurulmuş olan görünmez.
 */

/** Rıza fiilinin durumu. */
export const RIZA_DURUMU: Readonly<Record<string, string>> = {
  PENDING: 'Onay bekliyor',
  GRANTED: 'İzin verildi',
  WITHDRAWN: 'Geri çekildi',
};

/** Gönderim neden engellendi. Her biri ayrı bir olgu; tek bir "izinsiz" kutusuna toplanmıyor. */
export const ENGEL_SEBEBI: Readonly<Record<string, string>> = {
  NO_RECORD: 'Kayıt yok',
  PENDING_DOUBLE_OPT_IN: 'Onay bağlantısı tıklanmadı',
  WITHDRAWN: 'Geri çekildi',
  DENIED: 'Reddedildi',
  UNKNOWN: 'Sorulmamış (bilinmiyor)',
};

/**
 * Kanıtın **nerede durduğu**. İznin gerçekliğine dair bir yargı değil: bir denetçi "bu adrese neye
 * dayanarak yazdınız" diye sorduğunda cevabın nereyi göstereceği. İkisi de savunmadır.
 */
export const KANIT_GUCU: Readonly<Record<string, string>> = {
  PER_PERSON: 'Sistemde kayıtlı onay',
  OFFLINE_RECORD: 'İmzalı evrak (CRM dışında)',
  NONE: 'Kayıt yok',
};

export const RIZA_DAYANAGI: Readonly<Record<string, string>> = {
  LEDGER: 'Rıza defteri',
  V2_IMPORT: 'v2 aktarımı',
  NONE: 'Kayıt yok',
};

/**
 * Panelin dili. Rıza metinleri iki dilli (site iki dilli), CRM ekranı tek dilli — önizleme
 * hangisini göstereceğini seçmek zorunda ve Türkçesini gösteriyor.
 *
 * Ölçülen kusur (24.08.2026): seçim yapılmıyordu, `find` sıradaki ilk yürürlükteki satırı
 * alıyordu ve sıralama `locale` artan olduğu için Türkçe panelde İngilizce cümle görünüyordu.
 */
export const PANEL_DILI = 'tr';

export const KANAL: Readonly<Record<string, string>> = {
  EMAIL: 'E-posta',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
};

export const PAZARLAMA_METIN = {
  riza: {
    ustBaslik: 'BAE PDPL',
    baslik: 'Açık rıza defteri',
    tarif:
      'Her satır bir fiildir, bir durum değil: verilen ve geri çekilen izinler üst üste yazılmaz, ' +
      'arka arkaya eklenir. Bir kişinin bugünkü cevabı en yeni fiilidir.',
    hata: 'Rıza defteri okunamadı.',
    sutunKisi: 'Kişi',
    sutunKanal: 'Kanal',
    sutunDurum: 'Durum',
    sutunAn: 'Fiilin anı',
    sutunKaynak: 'Kaynak',
    sutunMetin: 'Metin sürümü',
    sutunKayit: 'Kaydeden',
    bosListe: 'Bu süzgeçle hiçbir fiil yok.',
    tumKanallar: 'Tüm kanallar',
    tumDurumlar: 'Tüm durumlar',
  },
  sorgu: {
    baslik: 'Kişi geçmişi',
    tarif:
      'Bir adres ya da telefon numarası girin: o kişinin her fiili, hangi metne razı olduğu ve ' +
      'bugün yazılıp yazılamayacağı. Adres sorgu dizesine değil istek gövdesine gider.',
    alan: 'Adres ya da telefon',
    dugme: 'Geçmişi getir',
    bosSonuc: 'Bu kişi için hiçbir kayıt yok. Kayıt yoksa izin de yoktur.',
    guncelBaslik: 'Bugünkü cevap',
    yazilabilir: 'Yazılabilir',
    yazilamaz: 'Yazılamaz',
    dayanak: 'Dayanak',
    kanit: 'Kanıt',
    beri: 'Şu andan beri',
  },
  cikar: {
    baslik: 'Geri çekmeyi kaydet',
    tarif:
      'Telefonda ya da e-postayla gelen bir çıkma talebi buradan kaydedilir. Karşılığı olan bir ' +
      '"izin ver" düğmesi yok ve olmayacak: rıza kişinin fiilidir, kimse onun adına veremez.',
    sebep: 'Not (isteğe bağlı)',
    dugme: 'Geri çekmeyi kaydet',
    tamam: 'Geri çekme deftere yazıldı.',
    hata: 'Geri çekme kaydedilemedi.',
  },
  metinler: {
    baslik: 'Rıza metinleri',
    tarif:
      'Onay kutusunun yanında hangi cümlenin durduğu, sürümüyle. Metin değiştiğinde eski sürüm ' +
      'silinmez: o güne kadar verilmiş rızalar onu göstermeye devam eder.',
    sutunKanal: 'Kanal',
    sutunSurum: 'Sürüm',
    sutunDil: 'Dil',
    sutunOzet: 'Onay kutusunun cümlesi',
    sutunGecerlilik: 'Yürürlük',
    yururlukte: 'Yürürlükte',
    eski: 'Eski sürüm',
    ileri: 'İleri tarihli',
    bos: 'Hiç metin yayımlanmamış. Metin olmadan hiçbir rıza kaydedilemez.',
    yayimla: 'Yeni sürüm yayımla',
    surumAlani: 'Sürüm etiketi',
    ozetAlani: 'Onay kutusunun cümlesi',
    govdeAlani: 'Tam metin',
    tamam: 'Yeni sürüm yayımlandı.',
    hata: 'Sürüm yayımlanamadı.',
  },
  form: {
    baslik: 'Kanal bazlı onay formu',
    tarif:
      'Web sitesindeki formun CRM tarafındaki eşi. Üç kutu da boş başlar: önceden işaretli bir ' +
      'kutu, kişinin vermediği bir izni vermiş gibi göstermenin en kısa yoludur. Buradan kayıt ' +
      'yapılmaz; ekran, gönderilecek metnin kanal başına ayrı olduğunu ve hiçbirinin varsayılan ' +
      'olarak açık gelmediğini gösterir.',
    kutuYok: 'Bu kanal için yayımlanmış metin yok; kutu çizilmiyor.',
    uyari:
      'Bu form yalnız önizlemedir. Rızayı kişi kendi verir: web formundan ya da üye panelinden, ' +
      've e-postada onay bağlantısı tıklanmadan izin tamamlanmaz.',
  },
  kapi: {
    baslik: 'Gönderim öncesi kapı',
    tarif:
      'Bir kitlenin kaçına gerçekten yazılabileceği. Adresi olmak izin değildir; bu sayı rıza ' +
      'defterinden geçer ve izinsiz hiç kimse içeri girmez.',
    kanal: 'Kanal',
    kampanya: 'Kampanya etiketi',
    dugme: 'Kitleyi ölç',
    aday: 'Aday',
    izinli: 'İzinli',
    engelli: 'Engelli',
    kesildi: 'Kitle tavana ulaştı, sayı eksik olabilir.',
    hata: 'Kitle ölçülemedi.',
  },
  sepet: {
    ustBaslik: 'Sepeti olan müşteri',
    baslik: 'Sepette bekleyen teklifler',
    tarif:
      'Satış akışı vakası madde 14-15: teklifi süresi dolan ve geri dönmeyen müşteri. Süresi ' +
      'dolmuş her teklif hemen buraya düşmez — müşteri hâlâ satışla konuşuyor olabilir, o yüzden ' +
      'bir bekleme penceresi var; aşağıdaki eşik odur.',
    hata: 'Sepet segmenti ölçülemedi.',
    olcutBasligi: 'Ölçüt',
    ulke: 'Ülke kodu',
    dil: 'Dil kodu',
    musteriTuru: 'Müşteri tipi',
    olc: 'Segmenti ölç',
    toplamTeklif: 'Bekleyen teklif',
    toplamMusteri: 'Müşteri sayısı',
    bekleyenPencere: 'Bekleme eşiği',
    dakika: 'dakika',
    enEski: 'En eski bekleyen',
    enYeni: 'En yeni bekleyen',
    ortalamaBeklemeGunu: 'Ortalama bekleme',
    gun: 'gün',
    kirilimUlke: 'Ülkeye göre',
    kirilimDil: 'Dile göre',
    kirilimTur: 'Müşteri tipine göre',
    kirilimKalem: 'Kalem türüne göre',
    kirilimParaBirimi: 'Para birimine göre',
    indirimBilgisi: (adet: string) => `${adet} teklifte satışçının elle girdiği bir indirim var. Otomatik bir sepet kampanyası yok.`,
    bosSonuc: 'Bu süzgeçte bekleyen sepet yok. Boş sayı sıfır satır demek: ya sepet yok, ya hepsi eşiğin altında bekliyor, ya da hepsi yenilendi/satın alındı.',
    gizlilikNotu:
      'Bu ekran yalnız sayar. İsim, e-posta ve telefon bu ölçümden hiç dönmez; kişi listesi ' +
      'yalnız gönderim anında, sunucu tarafında okunur.',
    gonderimBasligi: 'Hatırlatma gönder',
    gonderimTarif: 'Aynı ölçütle, sepetindekinin durumuna dair bir mesaj gönderir. Adres listesi ekrana hiç gelmez.',
    kanal: 'Kanal',
    kampanya: 'Kampanya etiketi',
    onceOlcOnizle: 'Önce ölç (göndermeden)',
    gonder: 'Gönder',
    gonderiliyor: 'Gönderiliyor…',
    aday: 'Aday',
    gonderildi: 'Gönderildi',
    adressiz: 'Adresi yok',
    izinsiz: 'İzin yok',
    yapilandirilmamis: 'Yapılandırılmamış',
    basarisiz: 'Gönderim hatası',
    dryRunSonuc: 'Bu bir önizleme — hiçbir mesaj gitmedi. Gerçekten göndermek için "Gönder"e basın.',
    gonderimTamam: 'Gönderim turu tamamlandı.',
    hataGonderim: 'Gönderim başlatılamadı.',
    kanalYapilandirilmamisBaslik: 'Bu kanaldan hiçbir gönderim denenmedi',
    /**
     * P1-5 (denetim, 07.09.2026): "Gönder" düğmesi kanalın yapılandırılıp yapılandırılmadığından
     * bağımsız olarak hep etkindi; kullanıcı deniyor, sonucu ancak istekten sonra
     * `NOT_CONFIGURED` olarak görüyordu. MARKETING rolü `integration:read` tutmadığı için
     * `/whatsapp/status`u kendi göremiyor — tek güvenilir sinyal, zaten var olan "Önce ölç"
     * önizlemesinin kendi cevabı. Düğme artık bu kanal için bir önizleme yapılıp kanalın
     * `CONFIGURED` döndüğü görülene kadar kapalı.
     */
    gonderOnceOnizle: 'Göndermeden önce "Önce ölç" ile bu kanalın yapılandırılmış olduğunu doğrulayın.',
  },
  gunluk: {
    baslik: 'Pazarlama denetim izi',
    tarif:
      'Pazarlamada olan her şey: kim, ne zaman, ne yaptı, hangi kayıt üzerinde. Silinemez — ' +
      'veritabanı bu tabloda UPDATE ve DELETE reddediyor.',
    sutunAn: 'Zaman',
    sutunEylem: 'Eylem',
    sutunAktor: 'Kim',
    sutunHedef: 'Kayıt',
    sutunSonuc: 'Sonuç',
    bos: 'Bu süzgeçle hiçbir kayıt yok.',
    hata: 'Denetim izi okunamadı.',
    tumEylemler: 'Tüm eylemler',
  },
  bulten: {
    ustBaslik: 'Bülten',
    baslik: 'Bülten aboneleri',
    /**
     * Ürünün adı ekranda **yalnız "web sitesi"**.
     *
     * `v3` bir iç sürüm numarası ve Ürün Sahibi'nin kararı kullanıcının onu hiç görmemesi
     * (`onyuz-kalite.spec.ts` 8. madde). Menüde bir kez düzeltilmişti; ölçüldü 28.08.2026,
     * aynı kelime bu iki cümlede duruyordu ve Pazarlama ekranında yazıyordu.
     */
    tarif:
      'Listeyi CRM tutar, web sitesi yalnız formu bildirir: widget, spam katmanları ve onay ' +
      'e-postası. "Onay bekliyor" durumundaki adres henüz abone değildir, yalnız formu ölçer.',
    sutunEposta: 'E-posta',
    sutunDurum: 'Durum',
    sutunOnayAni: 'Onay anı',
    sutunKaynakSayfa: 'Kaynak sayfa',
    sutunSonOlay: 'Son olay',
    tumDurumlar: 'Tüm durumlar',
    bos: 'Bu süzgeçle hiçbir abone yok.',
    hata: 'Bülten listesi okunamadı.',
    durum: {
      PENDING: 'Onay bekliyor',
      CONFIRMED: 'Onaylı abone',
      UNSUBSCRIBED: 'Abonelikten çıktı',
      SUPPRESSED: 'Gönderim engellendi',
    } as Readonly<Record<string, string>>,
    mutabakat: {
      baslik: 'Mutabakat durumu',
      tarif:
        'Web sitesi, altı saatte bir bu listeyi kendi kaydıyla karşılaştırıp bildirmeli. Burada, ' +
        'CRM’in kendi tuttuğu son turun ne zaman koştuğu görünür; site tarafındaki çağrı ölçülmez.',
      sonTur: 'Son mutabakat turu',
      hicKosmadi: 'Hiç koşmadı',
      saatOnce: 'saat önce',
      pencere: 'Söz verilen pencere',
      saat: 'saat',
      zamaninda: 'Pencere içinde',
      geciken: 'Pencere kaçırıldı',
      hata: 'Mutabakat durumu okunamadı.',
      sutunAn: 'Çalıştığı an',
      sutunKimden: 'Kimden',
      sutunIddiaEdilen: 'İddia edilen',
      sutunEslesen: 'Eşleşen',
      sutunEksik: 'Eksik',
      sutunFarkli: 'Farklı',
      sutunSonuc: 'Sonuç',
      temiz: 'Temiz',
      sorunlu: 'İncelensin',
      bos: 'Hiç mutabakat turu koşmamış.',
      hataListe: 'Mutabakat kayıtları okunamadı.',
    },
  },
} as const;
